import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  where,
  getDocs,
  query,
  collection,
  orderBy,
  addDoc,
  updateDoc,
  doc,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/helpers/firebase/firebase";
import { Collections } from "@/helpers/utils/collections";
import {
  CRM_COLLECTIONS,
  getVerticalForFormId,
} from "@/helpers/utils/crm-constants";
import {
  CRMParticipant,
  CRMActivity,
  CRMNote,
  VerticalCount,
  CRMVerticalKey,
} from "@/models/crm-types";
import { FormResponseStatus } from "@/models/form-types";
import { fetchAuthedJson } from "@/lib/news/auth-client";

// ─── CRM API ────────────────────────────────────────────────────────

const crmApi = createApi({
  reducerPath: "crmApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["CrmVerticalCounts", "CrmParticipants", "CrmActivities", "CrmNotes"],
  endpoints: (build) => ({

    // ── Dashboard: get counts for all verticals ──────────────────
    getCrmVerticalCounts: build.query<VerticalCount[], void>({
      async queryFn() {
        try {
          const response = await fetchAuthedJson<{ counts: VerticalCount[] }>(
            "/api/admin/crm/counts"
          );
          return { data: response.counts };
        } catch (e) {
          console.error("CRM getCrmVerticalCounts error:", e);
          return { error: e };
        }
      },
      providesTags: ["CrmVerticalCounts"],
    }),

    // ── Vertical pipeline: get participants by vertical ──────────
    getCrmParticipantsByVertical: build.query<
      CRMParticipant[],
      { verticalKey: CRMVerticalKey; personaKey?: string; statusFilter?: FormResponseStatus | "ALL" }
    >({
      async queryFn({ verticalKey, personaKey, statusFilter }) {
        try {
          const params = new URLSearchParams({ verticalKey });
          if (personaKey) params.set("personaKey", personaKey);
          if (statusFilter) params.set("statusFilter", statusFilter);

          const response = await fetchAuthedJson<{
            participants: CRMParticipant[];
          }>(`/api/admin/crm/participants?${params.toString()}`);

          return { data: response.participants };
        } catch (e) {
          console.error("CRM getCrmParticipantsByVertical error:", e);
          return { error: e };
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: "CrmParticipants", id: arg.verticalKey },
      ],
    }),

    // ── Activities for a participant ─────────────────────────────
    getCrmActivities: build.query<CRMActivity[], string>({
      async queryFn(participantUid) {
        try {
          const q = query(
            collection(db, CRM_COLLECTIONS.ACTIVITY_LOG),
            where("participantUid", "==", participantUid),
            orderBy("createdAt", "desc")
          );
          const snapshot = await getDocs(q);
          const activities: CRMActivity[] = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate?.()
              ? d.data().createdAt.toDate().toISOString()
              : new Date().toISOString(),
          })) as CRMActivity[];

          return { data: activities };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, uid) => [
        { type: "CrmActivities", id: uid },
      ],
    }),

    // ── Notes for a participant ──────────────────────────────────
    getCrmNotes: build.query<CRMNote[], string>({
      async queryFn(participantUid) {
        try {
          const q = query(
            collection(db, CRM_COLLECTIONS.PARTICIPANT_NOTES),
            where("participantUid", "==", participantUid),
            orderBy("createdAt", "desc")
          );
          const snapshot = await getDocs(q);
          const notes: CRMNote[] = snapshot.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              createdAt: data.createdAt?.toDate?.()
                ? data.createdAt.toDate().toISOString()
                : new Date().toISOString(),
              updatedAt: data.updatedAt?.toDate?.()
                ? data.updatedAt.toDate().toISOString()
                : new Date().toISOString(),
            };
          }) as CRMNote[];

          return { data: notes };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, uid) => [
        { type: "CrmNotes", id: uid },
      ],
    }),

    // ── Add a note ───────────────────────────────────────────────
    addCrmNote: build.mutation<
      CRMNote,
      { participantUid: string; authorUid: string; authorName: string; content: string }
    >({
      async queryFn({ participantUid, authorUid, authorName, content }) {
        try {
          const now = Timestamp.now();
          const noteData = {
            participantUid,
            authorUid,
            authorName,
            content,
            pinned: false,
            createdAt: now,
            updatedAt: now,
          };

          const docRef = await addDoc(
            collection(db, CRM_COLLECTIONS.PARTICIPANT_NOTES),
            noteData
          );

          // Also log as activity
          await addDoc(collection(db, CRM_COLLECTIONS.ACTIVITY_LOG), {
            participantUid,
            type: "note",
            title: "Note added",
            detail: content.substring(0, 120),
            actorUid: authorUid,
            actorName: authorName,
            createdAt: now,
          });

          return {
            data: {
              id: docRef.id,
              participantUid,
              authorUid,
              authorName,
              content,
              pinned: false,
              createdAt: now.toDate().toISOString(),
              updatedAt: now.toDate().toISOString(),
            },
          };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: "CrmNotes", id: arg.participantUid },
        { type: "CrmActivities", id: arg.participantUid },
      ],
    }),

    // ── Update a single form response status ─────────────────────
    updateFormResponseStatus: build.mutation<
      void,
      {
        uid: string;
        formResponseId: string;
        newStatus: FormResponseStatus;
        previousStatus: FormResponseStatus;
        actorUid: string;
        actorName: string;
        formId: string;
      }
    >({
      async queryFn({
        uid,
        formResponseId,
        newStatus,
        previousStatus,
        actorUid,
        actorName,
        formId,
      }) {
        try {
          const docRef = doc(
            db,
            Collections.USERS,
            uid,
            Collections.FORM_RESPONSES,
            formResponseId
          );
          await updateDoc(docRef, { status: newStatus });

          // Log activity
          const vertical = getVerticalForFormId(formId);
          await addDoc(collection(db, CRM_COLLECTIONS.ACTIVITY_LOG), {
            participantUid: uid,
            type: "status_change",
            title: `Status changed: ${previousStatus} → ${newStatus}`,
            metadata: {
              formId,
              previousStatus,
              newStatus,
              vertical: vertical ?? "unknown",
            },
            actorUid,
            actorName,
            createdAt: Timestamp.now(),
          });

          return { data: undefined };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: ["CrmVerticalCounts", "CrmParticipants"],
    }),

    // ── Bulk update form response statuses ───────────────────────
    bulkUpdateFormResponseStatus: build.mutation<
      void,
      {
        updates: {
          uid: string;
          formResponseId: string;
          formId: string;
          previousStatus: FormResponseStatus;
        }[];
        newStatus: FormResponseStatus;
        actorUid: string;
        actorName: string;
      }
    >({
      async queryFn({ updates, newStatus, actorUid, actorName }) {
        try {
          // Firestore batches limited to 500 writes
          const batchSize = 250; // Leave room for activity logs (2 writes per item)
          for (let i = 0; i < updates.length; i += batchSize) {
            const chunk = updates.slice(i, i + batchSize);
            const batch = writeBatch(db);

            for (const u of chunk) {
              const docRef = doc(
                db,
                Collections.USERS,
                u.uid,
                Collections.FORM_RESPONSES,
                u.formResponseId
              );
              batch.update(docRef, { status: newStatus });

              // Log activity for each
              const logRef = doc(collection(db, CRM_COLLECTIONS.ACTIVITY_LOG));
              const vertical = getVerticalForFormId(u.formId);
              batch.set(logRef, {
                participantUid: u.uid,
                type: "status_change",
                title: `Bulk status change: ${u.previousStatus} → ${newStatus}`,
                metadata: {
                  formId: u.formId,
                  previousStatus: u.previousStatus,
                  newStatus,
                  vertical: vertical ?? "unknown",
                },
                actorUid,
                actorName,
                createdAt: Timestamp.now(),
              });
            }

            await batch.commit();
          }

          return { data: undefined };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: ["CrmVerticalCounts", "CrmParticipants"],
    }),
  }),
});

export const {
  useGetCrmVerticalCountsQuery,
  useGetCrmParticipantsByVerticalQuery,
  useGetCrmActivitiesQuery,
  useGetCrmNotesQuery,
  useAddCrmNoteMutation,
  useUpdateFormResponseStatusMutation,
  useBulkUpdateFormResponseStatusMutation,
} = crmApi;

export default crmApi;
